import * as THREE from 'three';

/**
 * Joint-based hand gesture tracking for the MR range, WebXR hand-input API.
 *
 * The core problem this solves: the player's hands are busy *playing golf*.
 * Wrapped around a club grip, thumb and index naturally touch — which the
 * system happily reports as a pinch — and a swing moves the hands far faster
 * than any deliberate gesture. The SwingGuard classifies "golf hands" vs
 * "UI hands" so gestures only land when the player is plausibly gesturing.
 */

export type Handedness = 'left' | 'right';

// Gesture thresholds (meters / seconds), tuned for Quest hand tracking
const PINCH_START = 0.02;
const PINCH_END = 0.035;          // hysteresis so a pinch doesn't flicker
const MIDDLE_PINCH_START = 0.025;
const MIDDLE_PINCH_END = 0.045;
/** Avg middle/ring/pinky tip → wrist distance below this = fingers wrapped around something */
const GRIP_CURL_DIST = 0.11;
/** Both wrists closer than this = two-hand club grip (address/swing) */
const TWO_HAND_GRIP_DIST = 0.25;
/**
 * Wrist speed above this = swinging, not gesturing. Deliberately high: a
 * reach-to-pinch peaks around 1.5-2 m/s and must NOT trip the guard; a real
 * swing is 5-10 m/s at the wrist. Requires consecutive fast frames so a
 * single tracking spike can't suppress a legit gesture.
 */
const SWING_SPEED = 3.0;
const SWING_SPEED_FRAMES = 2;
/** Keep suppressing this long after the last golf-hands signal */
const GUARD_COOLDOWN_S = 0.5;

const TRACKED_JOINTS = [
  'wrist',
  'thumb-tip',
  'index-finger-tip',
  'middle-finger-tip',
  'ring-finger-tip',
  'pinky-finger-tip',
] as const;

type JointName = typeof TRACKED_JOINTS[number];

export class HandGestureTracker {
  readonly handedness: Handedness;
  tracked = false;
  /** Index pinch (thumb tip ↔ index tip), with hysteresis. */
  pinching = false;
  /** Thumb ↔ middle-finger pinch — the secondary "button". */
  middlePinching = false;
  /** Middle/ring/pinky curled toward the wrist — hand is holding something. */
  gripCurl = false;
  /** Smoothed wrist speed (m/s). */
  speed = 0;
  wrist = new THREE.Vector3();
  wristQuaternion = new THREE.Quaternion();
  /**
   * Direction the palm faces (world space). WebXR hand-joint spaces point +Y
   * toward the back of the hand, so this is wrist-orientation · (0,-1,0).
   */
  palmNormal = new THREE.Vector3(0, -1, 0);
  /**
   * Direction along the hand toward the fingertips (world space). WebXR joint
   * spaces point -Z at the next joint out, so this is wrist-orientation ·
   * (0,0,-1) — rigid and jitter-free, unlike deriving it from a fingertip
   * position, which swings as the fingers curl.
   */
  handForward = new THREE.Vector3(0, 0, -1);

  #joints = new Map<JointName, THREE.Vector3>();
  #prevWrist = new THREE.Vector3();
  #hasPrev = false;
  #fastFrames = 0;

  constructor(handedness: Handedness) {
    this.handedness = handedness;
    for (const name of TRACKED_JOINTS) this.#joints.set(name, new THREE.Vector3());
  }

  /** Consecutive frames of swing-speed movement (see SWING_SPEED_FRAMES). */
  get fastFrames() {
    return this.#fastFrames;
  }

  /** Index fingertip position (live reference — read-only, world space). */
  get indexTip(): THREE.Vector3 {
    return this.#joints.get('index-finger-tip')!;
  }

  update(frame: XRFrame, referenceSpace: XRReferenceSpace, hand: XRHand | undefined, dt: number) {
    const getJointPose = (frame as any).getJointPose?.bind(frame);
    if (!hand || !getJointPose) {
      this.#reset();
      return;
    }

    // A fingertip occluded behind a grip is common — keep its last position and
    // carry on. The wrist is the one joint the pose can't be rebuilt without.
    //
    // The tolerance is TWO missing tips, not one, because of what a pinch
    // physically is: thumb and index tips meeting is exactly the case where
    // both drop out of the tracker together. At a tolerance of one, the gesture
    // destroyed its own tracking — `tracked` went false, SwingGuard suppressed
    // the hand for that reason alone, and the pinch was discarded as "golf
    // hands". The gesture cannot be allowed to disqualify itself.
    let missing = 0;
    for (const name of TRACKED_JOINTS) {
      const space = (hand as any).get?.(name);
      const pose = space ? getJointPose(space, referenceSpace) : null;
      if (!pose) {
        missing++;
        if (name === 'wrist' || missing > 2) {
          this.#reset();
          return;
        }
        continue; // keep the joint's previous position
      }
      const p = pose.transform.position;
      this.#joints.get(name)!.set(p.x, p.y, p.z);
      if (name === 'wrist') {
        const o = pose.transform.orientation;
        this.wristQuaternion.set(o.x, o.y, o.z, o.w);
        this.palmNormal.set(0, -1, 0).applyQuaternion(this.wristQuaternion);
        this.handForward.set(0, 0, -1).applyQuaternion(this.wristQuaternion);
      }
    }

    this.tracked = true;
    const wrist = this.#joints.get('wrist')!;
    this.wrist.copy(wrist);

    // Wrist speed (EMA-smoothed — raw hand tracking jitters)
    if (this.#hasPrev && dt > 0) {
      const instant = this.#prevWrist.distanceTo(wrist) / dt;
      this.speed = this.speed * 0.7 + instant * 0.3;
    }
    this.#prevWrist.copy(wrist);
    this.#hasPrev = true;
    this.#fastFrames = this.speed > SWING_SPEED ? this.#fastFrames + 1 : 0;

    const thumb = this.#joints.get('thumb-tip')!;
    const index = this.#joints.get('index-finger-tip')!;
    const middle = this.#joints.get('middle-finger-tip')!;
    const ring = this.#joints.get('ring-finger-tip')!;
    const pinky = this.#joints.get('pinky-finger-tip')!;

    const indexDist = thumb.distanceTo(index);
    this.pinching = this.pinching ? indexDist < PINCH_END : indexDist < PINCH_START;

    const middleDist = thumb.distanceTo(middle);
    const middleRaw = this.middlePinching ? middleDist < MIDDLE_PINCH_END : middleDist < MIDDLE_PINCH_START;
    // An index pinch drags the middle finger along — don't double-detect
    this.middlePinching = middleRaw && !this.pinching;

    // Club-grip signature: the three non-gesture fingers wrapped in. A
    // deliberate pinch keeps them extended, so this cleanly separates
    // "holding the club" from "pinching at the UI".
    const curl = (middle.distanceTo(wrist) + ring.distanceTo(wrist) + pinky.distanceTo(wrist)) / 3;
    this.gripCurl = curl < GRIP_CURL_DIST;
  }

  #reset() {
    this.tracked = false;
    this.pinching = false;
    this.middlePinching = false;
    this.gripCurl = false;
    this.speed = 0;
    this.#hasPrev = false;
    this.#fastFrames = 0;
  }
}

export type SwingGuardOptions = {
  /** External busy signal — e.g. a shot in flight suppresses everything. */
  isBusy?: () => boolean;
  /**
   * Skip the two-hand-grip term. Reaching across to poke a hand-mounted panel
   * brings the wrists as close together as an address grip does, which would
   * otherwise suppress both hands and dismiss the very panel being poked. Only
   * safe while something else already blocks shot-firing gestures.
   */
  ignoreTwoHandGrip?: () => boolean;
  /**
   * Drop the POSTURE terms — grip curl and two-hand grip — keeping the motion
   * and busy terms. For phases where no shot can be fired at all, so there is
   * nothing for the guard to protect.
   *
   * The tee-placement flow is the case this exists for. A player lining up the
   * tee is usually already holding their club, which pins `gripCurl` true and
   * suppresses the hand permanently — so every pinch was thrown away, and the
   * only way to place the tee was to put the club down first. Nothing was being
   * protected: a confirm during placement anchors the tee, it can't launch a
   * ball. Speed is still guarded, so a genuine swing is still ignored.
   */
  relaxed?: () => boolean;
};

/**
 * Decides when hand gestures must be ignored because the player is (about to
 * be) hitting a real golf ball. Suppression triggers on any of:
 *  - grip curl (fingers wrapped around the club)
 *  - two-hand grip (wrists stacked together at address)
 *  - swing-speed hand movement
 *  - lost tracking (Quest drops hands mid-swing; re-acquisition is noisy)
 *  - the external busy signal (shot in flight)
 * and holds for a cooldown after the last signal so tracking recovery can't
 * emit phantom gestures.
 */
export class SwingGuard {
  #isBusy: () => boolean;
  #ignoreTwoHandGrip: () => boolean;
  #relaxed: () => boolean;
  #cooldown = new Map<Handedness, number>([['left', 0], ['right', 0]]);
  #suppressed = new Map<Handedness, boolean>([['left', true], ['right', true]]);

  constructor(options: SwingGuardOptions = {}) {
    this.#isBusy = options.isBusy ?? (() => false);
    this.#ignoreTwoHandGrip = options.ignoreTwoHandGrip ?? (() => false);
    this.#relaxed = options.relaxed ?? (() => false);
  }

  update(dt: number, left: HandGestureTracker, right: HandGestureTracker) {
    const busy = this.#isBusy();
    const relaxed = this.#relaxed();
    const twoHandGrip = !relaxed && !this.#ignoreTwoHandGrip()
      && left.tracked && right.tracked
      && left.wrist.distanceTo(right.wrist) < TWO_HAND_GRIP_DIST;

    for (const tracker of [left, right]) {
      const hand = tracker.handedness;
      const golfHands = busy
        || !tracker.tracked
        || (tracker.gripCurl && !relaxed)
        || twoHandGrip
        || tracker.fastFrames >= SWING_SPEED_FRAMES;

      let cooldown = this.#cooldown.get(hand)! - dt;
      if (golfHands) cooldown = GUARD_COOLDOWN_S;
      this.#cooldown.set(hand, Math.max(0, cooldown));
      this.#suppressed.set(hand, golfHands || cooldown > 0);
    }
  }

  isSuppressed(handedness: Handedness | string): boolean {
    return this.#suppressed.get(handedness as Handedness) ?? true;
  }

  /** Every hand is currently being ignored (both suppressed/untracked). */
  get allSuppressed(): boolean {
    return this.isSuppressed('left') && this.isSuppressed('right');
  }

  /** At least one hand is currently being ignored. */
  get anySuppressed(): boolean {
    return this.isSuppressed('left') || this.isSuppressed('right');
  }
}
